import React, { ReactNode } from 'react';
import { Navigate, NonIndexRouteObject } from 'react-router-dom';

import { salesRoles } from 'config';
import { Icon } from 'components/icon';
import Shipments from 'pages/shipments';
import { Placeholder } from 'components/placeholder';
import { DocumentsPaths } from 'pages/documents/paths';
import { PayrollRouteGuard } from 'pages/payroll/guard';
import { Role, Roles, Feature, BackOfficeRoles } from 'types';
import {
  AccountingIndexRedirect,
  AccountingOverviewRouteGuard,
} from 'pages/accounting/overview/guard';
import {
  Deals,
  Users,
  Events,
  Themes,
  Devices,
  Company,
  Reports,
  Profile,
  Payroll,
  Settings,
  Products,
  Category,
  Storages,
  Carriers,
  Shipping,
  Dashboard,
  Customers,
  Inventory,
  Employees,
  Suppliers,
  Accounting,
  StockOrders,
  EventOrders,
  DailyCounts,
  CustomerCare,
  EventReviews,
  ProductTypes,
  Integrations,
  Notifications,
  EventInventory,
  StockTransfers,
  EventsCalendar,
  CreateShipping,
  CompanySettings,
  InventoryProducts,
  CustomerCareReceipt,
  EmployeeCompetition,
  AccountingCustomerCareReport,
} from 'pages';

import { Config } from './env';
import { AppRoutes } from './routes';

// ACCOUNTING OVERVIEW (plan §3, §5): its own lazy chunk.
const AccountingOverview = React.lazy(
  () => import('pages/accounting/overview')
);
// QuickBooks Control Center (Production build: master + the QuickBooks
// module only). Both pages are lazy chunks and both routes require the
// `quickbooks` feature, so for every user without it the route table, the
// sidebar and every POS screen are exactly master's.
const QuickBooksControlCenter = React.lazy(
  () => import('pages/accounting/quickbooks')
);
const QuickBooksSettingsPage = React.lazy(
  () => import('pages/company-settings/integrations/quickbooks/page')
);
// T-R11: the rental extra charges' link. QuickBooks users go on to the
// car-rental lane; Administrative (Accounting feature, no QuickBooks) reads
// the list there, read-only. A lazy chunk like the Control Center.
const RentalExtrasPage = React.lazy(
  () => import('pages/accounting/quickbooks/rental-extras-page')
);
// ONBOARDING plan 10 (owner 25 Oct 2026): the documents list and the
// template editor, their own lazy chunks (pdf.js loads only in the editor).
const OnboardingOrderPage = React.lazy(
  () => import('pages/company-settings/integrations/onboarding-templates/order')
);
const OnboardingTemplateEditor = React.lazy(
  () =>
    import('pages/company-settings/integrations/onboarding-templates/editor')
);
// Plan 10 / 11: New template, and a draft's order and publish steps.
const NewTemplatePage = React.lazy(
  () => import('pages/template-flow/new-template')
);
const TemplateOrderStep = React.lazy(
  () => import('pages/template-flow/order-step')
);
const TemplatePublishStep = React.lazy(
  () => import('pages/template-flow/publish-step')
);
// TIME OFF (owner 24 Oct 2026): the approvers' page, its own lazy chunk.
const TimeOffPage = React.lazy(() => import('pages/time-off/page'));
// BOOKING (owner 25 Oct 2026): its own lazy chunks.
const BookingLayout = React.lazy(() => import('pages/booking/layout'));
const BookingQueuePage = React.lazy(() => import('pages/booking/queue'));
const BookingYearPlanPage = React.lazy(() => import('pages/booking/year-plan'));
const BookingPaymentsPage = React.lazy(() => import('pages/booking/payments'));
const ResearchLayout = React.lazy(() => import('pages/research/layout'));
const ResearchListPage = React.lazy(() => import('pages/research'));
// RESEARCH MAIL: /mail forwards to Research's Mail tab.
const MailPage = React.lazy(() => import('pages/mail'));
// REP PERFORMANCE (owner 25 Oct 2026): the Rep Scoreboard, its own lazy chunk.
const RepScoreboardPage = React.lazy(() => import('pages/rep-scoreboard'));
// TRAINING (owner 25 Oct 2026, F-20 S3): its own lazy chunk.
const TrainingPage = React.lazy(() => import('pages/training'));
// DOCUMENTS (plan 11 D2–D4, 26 Oct 2026): its own lazy chunks.
const DocumentsLayout = React.lazy(() => import('pages/documents/layout'));
const DocumentsHomePage = React.lazy(() => import('pages/documents/home'));
const DocumentsAgreementsPage = React.lazy(
  () => import('pages/documents/agreements')
);
const DocumentPackagePage = React.lazy(() => import('pages/documents/package'));
const DocumentsTemplatesPage = React.lazy(
  () => import('pages/documents/templates')
);
const DocumentsSetEditorPage = React.lazy(
  () => import('pages/documents/templates/set-editor')
);
const DocumentsSendPage = React.lazy(() => import('pages/documents/send'));
// TRIPS (Event Logistics, owner 25 Oct 2026): their own lazy chunks.
const TripsPage = React.lazy(() => import('pages/trips'));
const TripPersonPage = React.lazy(() => import('pages/trips/person'));
const MyTripPage = React.lazy(() => import('pages/my-trip'));
const MyTripEventPage = React.lazy(() => import('pages/my-trip/event'));
const MyTripItemPage = React.lazy(() => import('pages/my-trip/item'));
const MyTripStockPage = React.lazy(() => import('pages/my-trip/stock'));

export interface RouteObject extends Omit<
  NonIndexRouteObject,
  'index' | 'children'
> {
  title?: string;
  index?: boolean;
  icon?: ReactNode;
  // TRIPS: the office Trips page for the `trips` tick (and a Manager leading
  // an event); "My trip" only for someone with a sent trip. Neither is ever the landing page (no switch).
  tripsOnly?: boolean;
  features?: Feature[];
  myTripOnly?: boolean;
  hideForRoles?: Role[];
  // BOOKING: listed only for the `booking` tick (no company switch).
  bookingOnly?: boolean;
  // RESEARCH: listed only for the `research` tick (no company switch).
  researchOnly?: boolean;
  // TRAINING: listed for the reader roles and for anyone training a rep.
  trainingOnly?: boolean;
  hideInSidebar?: boolean;
  children?: RouteObject[];
  // REP PERFORMANCE: listed only for the roles that may open the Rep
  // Scoreboard (no company switch).
  repPerformanceOnly?: boolean;
}

const getCommonProfileRoutes = (isEmployee?: boolean) => [
  {
    index: true,
    element: <Navigate replace to={AppRoutes.Profile.Payments} />,
  },
  {
    path: AppRoutes.Profile.Payments,
    children: [
      { index: true, element: <Profile.Payments /> },
      {
        element: <Profile.CreateReimbursements />,
        path: AppRoutes.Profile.Reimbursement.New,
      },
      {
        element: <Profile.EditReimbursements />,
        path: AppRoutes.Profile.Reimbursement.Edit,
      },
      {
        element: <Profile.ReimbursementDetails />,
        path: AppRoutes.Profile.Reimbursement.Details,
      },

      {
        element: <Profile.CreateAdvances />,
        path: AppRoutes.Profile.Advances.New,
      },
      {
        element: <Profile.AdvanceDetails />,
        path: AppRoutes.Profile.Advances.Details,
      },
      {
        element: <Profile.EditAdvances />,
        path: AppRoutes.Profile.Advances.Edit,
      },

      {
        element: <Profile.BonusDetails />,
        path: AppRoutes.Profile.Bonuses.Details,
      },
      {
        element: <Profile.CreateBonuses />,
        path: AppRoutes.Profile.Bonuses.New,
      },
      {
        element: <Profile.EditBonuses />,
        path: AppRoutes.Profile.Bonuses.Edit,
      },
    ],
  },
  {
    path: AppRoutes.Profile.Customers,
    element: <Profile.Customers isEmployee={isEmployee} />,
  },
  {
    path: AppRoutes.Profile.Sales,
    element: <Profile.SalesList />,
  },
  {
    element: <Profile.Refunds />,
    path: AppRoutes.Profile.Refunds,
  },
  {
    element: <Profile.ManageAppointments />,
    path: AppRoutes.Profile.ManageAppointments,
  },
  {
    element: <Profile.Documents />,
    path: AppRoutes.Profile.Documents,
  },
  // TIME OFF (owner 24 Oct 2026): the rep asks on their own profile
  // (time-off/new); approvers see an employee's requests on the employee's
  // profile (owner on Dev: "add tab here").
  {
    path: AppRoutes.Profile.TimeOff.Home,
    children: [
      { index: true, element: <Profile.TimeOff /> },
      ...(isEmployee
        ? []
        : [
            {
              path: AppRoutes.Profile.TimeOff.New,
              element: <Profile.TimeOff requestOpen />,
            },
          ]),
    ],
  },
];

export const getRouteObjects = (): RouteObject[] => {
  return [
    {
      icon: <Icon.User />,
      title: 'User Profile',
      element: <Profile.Home />,
      path: AppRoutes.Profile.Home,
      features: [Feature.USER_PROFILE],
      children: getCommonProfileRoutes(),
    },
    {
      title: 'Dashboard',
      icon: <Icon.ActionCenter />,
      path: AppRoutes.Dashboard.Analytics.Home,
      features: [Feature.DASHBOARD, Feature.ANALYTICS],
      hideForRoles: [BackOfficeRoles.MANAGER, BackOfficeRoles.SALES_REP],
      children: [
        {
          index: true,
          element: <Dashboard.Analytics />,
        },
      ],
    },
    {
      title: 'Companies',
      icon: <Icon.UserDual />,
      path: AppRoutes.Company.Home,
      features: [Feature.COMPANIES],
      hideForRoles: [Roles.COMPANY_OWNER],
      children: [
        {
          index: true,
          element: <Company.List />,
        },
        {
          path: AppRoutes.Company.New,
          element: <Company.Create />,
        },
        {
          element: <Company.Edit />,
          path: AppRoutes.Company.Edit,
        },
        {
          element: <Company.Details />,
          path: AppRoutes.Company.Details,
        },
      ],
    },
    {
      title: 'Accounting',
      icon: <Icon.Folder />,
      element: <Accounting.Home />,
      path: AppRoutes.Accounting.Home,
      // Owner 30 Oct 2026: `add_expenses` opens only Expenses here (the page
      // sends anything else there; accounting/home).
      features: [Feature.ACCOUNTING, Feature.ADD_EXPENSES],
      children: [
        {
          // ACCOUNTING OVERVIEW (plan §5, Q6): the Overview for its three
          // viewer roles, Expenses for everyone else (as before).
          index: true,
          element: <AccountingIndexRedirect />,
        },
        {
          path: AppRoutes.Accounting.Overview.Home,
          element: <AccountingOverviewRouteGuard />,
          children: [{ index: true, element: <AccountingOverview /> }],
        },
        {
          path: AppRoutes.Accounting.Expenses.Home,
          children: [
            {
              index: true,
              element: <Accounting.Expenses />,
            },
            {
              path: AppRoutes.Accounting.Expenses.New,
              element: <Accounting.CreateAccountingExpenses />,
            },
            {
              path: AppRoutes.Accounting.Expenses.Edit,
              element: <Accounting.EditAccountingExpenses />,
            },
            {
              element: <Accounting.ExpensesDetails />,
              path: AppRoutes.Accounting.Expenses.Details,
            },
            {
              element: <Accounting.ExpensesCategories />,
              path: AppRoutes.Accounting.Expenses.Categories,
            },
          ],
        },
        {
          element: <Accounting.Deposit />,
          path: AppRoutes.Accounting.Deposit.Home,
        },
        {
          element: <Accounting.Sales />,
          path: AppRoutes.Accounting.Sales.Home,
        },
        {
          element: <Accounting.Transactions />,
          path: AppRoutes.Accounting.Transactions.Home,
        },
        {
          element: <Accounting.Chargebacks />,
          path: AppRoutes.Accounting.Chargebacks.Home,
        },
        {
          features: [Feature.QUICKBOOKS],
          element: <QuickBooksControlCenter />,
          path: AppRoutes.Accounting.QuickBooks.Home,
        },
        {
          element: <RentalExtrasPage />,
          features: [Feature.ACCOUNTING],
          path: AppRoutes.Accounting.RentalExtras.Home,
        },
        {
          features: [Feature.CUSTOMER_SUPPORT],
          element: <AccountingCustomerCareReport />,
          path: AppRoutes.Accounting.CustomerCareReport.Home,
        },
        {
          element: <Accounting.Retrievals />,
          path: AppRoutes.Accounting.Retrievals.Home,
        },
        {
          element: <Accounting.GiftCards />,
          path: AppRoutes.Accounting.GiftCards.Home,
        },
        {
          element: <Accounting.Tavla.Home />,
          path: AppRoutes.Accounting.Tavla.Home,
          children: [
            {
              index: true,
              element: (
                <Navigate replace to={AppRoutes.Accounting.Tavla.Summary} />
              ),
            },
            {
              element: <Accounting.Tavla.Summary />,
              path: AppRoutes.Accounting.Tavla.Summary,
            },
            {
              element: <Accounting.Tavla.BackOffice />,
              path: AppRoutes.Accounting.Tavla.BackOffice,
            },
            {
              element: <Accounting.Tavla.Transactions />,
              path: AppRoutes.Accounting.Tavla.Transactions,
            },
            {
              element: <Accounting.Tavla.Payments />,
              path: AppRoutes.Accounting.Tavla.Payments,
            },
          ],
        },
        {
          element: <Accounting.Revenue.Home />,
          path: AppRoutes.Accounting.Revenue.Home,
          children: [
            {
              index: true,
              element: (
                <Navigate replace to={AppRoutes.Accounting.Revenue.Deposit} />
              ),
            },
            {
              element: <Accounting.Revenue.Deposit />,
              path: AppRoutes.Accounting.Revenue.Deposit,
            },
            {
              element: <Accounting.Revenue.Summary />,
              path: AppRoutes.Accounting.Revenue.Summary,
            },
            {
              element: <Accounting.Revenue.Credit />,
              path: AppRoutes.Accounting.Revenue.Credit,
            },
          ],
        },
        {
          element: <Accounting.SalesTax.Home />,
          path: AppRoutes.Accounting.SalesTax.Home,
          children: [
            {
              index: true,
              element: (
                <Navigate replace to={AppRoutes.Accounting.SalesTax.Taxes} />
              ),
            },
            {
              element: <Accounting.SalesTax.Taxes />,
              path: AppRoutes.Accounting.SalesTax.Taxes,
            },
            {
              element: <Accounting.SalesTax.Summary />,
              path: AppRoutes.Accounting.SalesTax.Summary,
            },
          ],
        },
        {
          element: <Accounting.ProfitAndLosses />,
          path: AppRoutes.Accounting.ProfitAndLosses.Home,
        },
        {
          path: AppRoutes.Accounting.CommissionStructure.Home,
          children: [
            {
              index: true,
              element: <Accounting.CommissionStructe.List />,
            },
            {
              element: <Accounting.CommissionStructe.Details />,
              path: AppRoutes.Accounting.CommissionStructure.Details,
            },
            {
              element: <Accounting.CommissionStructe.Create />,
              path: AppRoutes.Accounting.CommissionStructure.Create,
            },
            {
              element: <Accounting.CommissionStructe.Edit />,
              path: AppRoutes.Accounting.CommissionStructure.Edit,
            },
          ],
        },
        {
          element: <CreateShipping />,
          path: AppRoutes.Accounting.CreateShipping.Home,
        },
      ],
    },
    {
      title: 'Payroll',
      icon: <Icon.CreditCard />,
      // PAYROLL DASHBOARD (plan §5): the role's `payroll` tick (owner 2 Nov
      // 2026, Permissions); the guard is the route's own element, so the URL
      // is closed to every other role too.
      features: [Feature.PAYROLL],
      path: AppRoutes.Payroll.Home,
      element: <PayrollRouteGuard />,
      children: [{ index: true, element: <Payroll /> }],
    },
    {
      icon: <Icon.Case />,
      title: 'Customer care',
      element: <CustomerCare />,
      path: AppRoutes.CustomerCare.Home,
      features: [Feature.CUSTOMER_SUPPORT],
    },
    {
      // Read-only receipt for Customer care (owner 27 Oct, F4 option B):
      // "Open in POS" and the log rows land here. The selling receipt page
      // (Products) still needs Checkout.
      title: 'Receipt',
      hideInSidebar: true,
      icon: <Icon.Case />,
      element: <CustomerCareReceipt />,
      path: AppRoutes.CustomerCare.Receipt,
      features: [Feature.CUSTOMER_SUPPORT],
    },
    {
      title: 'Customers',
      icon: <Icon.UserDual />,
      path: AppRoutes.Customers.Home,
      features: [Feature.CUSTOMER_SUPPORT, Feature.CUSTOMERS, Feature.SALES],
      children: [
        {
          index: true,
          element: <Customers.List />,
        },
        {
          path: AppRoutes.Customers.New,
          element: <Customers.Create />,
        },
        {
          element: <Customers.Edit />,
          path: AppRoutes.Customers.Edit,
        },
        {
          element: <Customers.Details />,
          path: AppRoutes.Customers.Details,
        },
      ],
    },
    {
      title: 'Reports',
      element: <Reports />,
      icon: <Icon.FileInfo />,
      features: [Feature.REPORTS],
      path: AppRoutes.Reports.Home,
    },
    {
      title: 'Events',
      icon: <Icon.List />,
      features: [Feature.EVENTS],
      path: AppRoutes.Events.Home,
      children: [
        {
          index: true,
          element: <Events.List />,
        },
        {
          element: <Events.Create />,
          path: AppRoutes.Events.New,
        },
        {
          element: <Events.Edit />,
          path: AppRoutes.Events.Edit,
        },
        {
          path: AppRoutes.Events.Details,
          children: [
            {
              index: true,
              element: <Events.Details />,
            },
            {
              element: <Events.Questionnaire />,
              path: AppRoutes.Events.Questionnaire,
            },
          ],
        },
      ],
    },
    {
      title: 'Inventory',
      element: <Inventory />,
      icon: <Icon.SecondaryList />,
      features: [Feature.INVENTORY],
      path: AppRoutes.Inventory.Home,
      children: [
        {
          index: true,
          element: <Navigate replace to={AppRoutes.Inventory.Products.Home} />,
        },
        {
          title: 'Products',
          icon: <Icon.Barcode />,
          path: AppRoutes.Inventory.Products.Home,
          children: [
            {
              index: true,
              element: <InventoryProducts.List />,
            },
            {
              element: <InventoryProducts.Create />,
              path: AppRoutes.Inventory.Products.New,
            },
            {
              element: <InventoryProducts.Details />,
              path: AppRoutes.Inventory.Products.Details,
            },
            {
              element: <InventoryProducts.Edit />,
              path: AppRoutes.Inventory.Products.Edit,
            },
          ],
        },
        {
          title: 'Suppliers',
          icon: <Icon.UserFilled />,
          path: AppRoutes.Inventory.Suppliers.Home,
          children: [
            {
              index: true,
              element: <Suppliers.List />,
            },
            {
              element: <Suppliers.Create />,
              path: AppRoutes.Inventory.Suppliers.New,
            },
            {
              element: <Suppliers.Details />,
              path: AppRoutes.Inventory.Suppliers.Details,
            },
            {
              element: <Suppliers.Edit />,
              path: AppRoutes.Inventory.Suppliers.Edit,
            },
          ],
        },
        {
          title: 'Category',
          icon: <Icon.LongText />,
          path: AppRoutes.Inventory.Category.Home,
          children: [
            { index: true, element: <Category.List /> },
            {
              element: <Category.Create />,
              path: AppRoutes.Inventory.Category.New,
            },
            {
              element: <Category.Edit />,
              path: AppRoutes.Inventory.Category.Edit,
            },
          ],
        },
        {
          title: 'Storages',
          icon: <Icon.Storage />,
          path: AppRoutes.Inventory.Storages.Home,
          children: [
            {
              index: true,
              element: <Storages.List />,
            },
            {
              element: <Storages.Create />,
              path: AppRoutes.Inventory.Storages.New,
            },
            {
              element: <Storages.Edit />,
              path: AppRoutes.Inventory.Storages.Edit,
            },
            {
              element: <Storages.Details />,
              path: AppRoutes.Inventory.Storages.Details.Home,
              children: [
                {
                  index: true,
                  element: (
                    <Navigate
                      replace
                      to={AppRoutes.Inventory.Storages.Details.General}
                    />
                  ),
                },
                {
                  element: <Storages.General />,
                  path: AppRoutes.Inventory.Storages.Details.General,
                },
                {
                  path: AppRoutes.Inventory.Storages.Details.Inventory,
                  children: [
                    { index: true, element: <Storages.Inventory /> },
                    {
                      element: <Storages.InventoryDetails />,
                      path: AppRoutes.Inventory.Storages.Details
                        .InventoryDetails,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          title: 'Transfers',
          path: AppRoutes.Inventory.Transfers.Home,
          children: [
            {
              index: true,
              element: <StockTransfers.List />,
            },
            {
              element: <StockTransfers.Create />,
              path: AppRoutes.Inventory.Storages.New,
            },
            {
              element: <StockTransfers.Edit />,
              path: AppRoutes.Inventory.Storages.Edit,
            },
            {
              element: <StockTransfers.Details />,
              path: AppRoutes.Inventory.Storages.Details.Home,
            },
          ],
        },
        {
          title: 'Event Inventory',
          path: AppRoutes.Inventory.Event.Home,
          children: [
            {
              index: true,
              element: <EventInventory.List />,
            },
            {
              path: AppRoutes.Inventory.Event.Details.Home,
              children: [
                {
                  index: true,
                  element: <EventInventory.Details />,
                },
                {
                  element: <EventInventory.InventoryDetails />,
                  path: AppRoutes.Inventory.Event.Details.InventoryDetails,
                },
              ],
            },
          ],
        },
        {
          title: 'Daily Counts',
          element: <DailyCounts.List />,
          path: AppRoutes.Inventory.DailyCounts.Home,
        },
        {
          path: AppRoutes.Inventory.StockOrders.Home,
          children: [
            {
              index: true,
              element: <StockOrders.List />,
            },
            {
              element: <StockOrders.Create />,
              path: AppRoutes.Inventory.StockOrders.New.Home,
              children: [
                {
                  index: true,
                  element: (
                    <Navigate
                      replace
                      to={
                        AppRoutes.Inventory.StockOrders.New.Children.OrderInfo
                      }
                    />
                  ),
                },
                {
                  element: <StockOrders.OrderInfo />,
                  path: AppRoutes.Inventory.StockOrders.New.Children.OrderInfo,
                },
                {
                  element: <StockOrders.Products />,
                  path: AppRoutes.Inventory.StockOrders.New.Children.Products,
                },
              ],
            },
            {
              element: <StockOrders.Edit />,
              path: AppRoutes.Inventory.StockOrders.Edit.Home,
              children: [
                {
                  index: true,
                  element: (
                    <Navigate
                      replace
                      to={
                        AppRoutes.Inventory.StockOrders.Edit.Children.OrderInfo
                      }
                    />
                  ),
                },
                {
                  element: <StockOrders.OrderInfo />,
                  path: AppRoutes.Inventory.StockOrders.Edit.Children.OrderInfo,
                },
                {
                  element: <StockOrders.Products />,
                  path: AppRoutes.Inventory.StockOrders.Edit.Children.Products,
                },
              ],
            },
            {
              element: <StockOrders.Details />,
              path: AppRoutes.Inventory.StockOrders.Details,
            },
          ],
        },
        // POS Deals (owner, 26 Oct 2026): bundles the office sets up for
        // the register.
        {
          title: 'Deals',
          icon: <Icon.Deal />,
          path: AppRoutes.Inventory.Deals.Home,
          children: [
            {
              index: true,
              element: <Deals.List />,
            },
            {
              element: <Deals.Create />,
              path: AppRoutes.Inventory.Deals.New,
            },
            {
              element: <Deals.Edit />,
              path: AppRoutes.Inventory.Deals.Edit,
            },
          ],
        },
        // {
        //   title: 'Attributes',
        //   icon: <Icon.Deal />,
        //   path: AppRoutes.Inventory.Attributes.Home,
        //   children: [
        //     {
        //       index: true,
        //       element: <Attributes.List />,
        //     },
        //     {
        //       element: <Attributes.Create />,
        //       path: AppRoutes.Inventory.Attributes.New,
        //     },
        //     {
        //       element: <Attributes.Edit />,
        //       path: AppRoutes.Inventory.Attributes.Edit,
        //     },
        //   ],
        // },
      ],
    },
    {
      title: 'Products',
      hideInSidebar: true,
      icon: <Icon.Folder />,
      features: [Feature.CHECKOUT],
      path: AppRoutes.Products.Home,
      children: [
        {
          index: true,
          element: <Products.List />,
        },
        { element: <Products.CheckOut />, path: AppRoutes.Products.CheckOut },
        {
          element: <Products.ExchangeSuccess />,
          path: AppRoutes.Products.ExchangeSuccess,
        },
        {
          // Owner 25 Oct 2026: no Employees list for a Sales Rep, by URL too.
          element: <Products.Employees />,
          path: AppRoutes.Products.Employees,
          hideForRoles: [BackOfficeRoles.SALES_REP],
        },
        {
          element: <Products.CashManager />,
          path: AppRoutes.Products.CashManager,
        },
        {
          element: <Products.Dashboard />,
          path: AppRoutes.Products.Dashboard,
        },
        {
          path: AppRoutes.Products.Inventory,
          children: [
            {
              index: true,
              element: <Products.Inventory />,
            },
          ],
        },
        {
          element: <Products.EmployeeCompetition />,
          path: AppRoutes.Products.EmployeeCompetition,
        },
        {
          element: <Products.Customers />,
          path: AppRoutes.Products.Customers.Home,
          children: [
            {
              index: true,
              element: <Placeholder text="Please select a customer" />,
            },
            {
              element: <Products.CustomerDetails />,
              path: AppRoutes.Products.Customers.Details,
            },
            {
              element: <Products.CustomerEdit />,
              path: AppRoutes.Products.Customers.Edit,
            },
            {
              element: <Products.CustomerNew />,
              path: AppRoutes.Products.Customers.New,
            },
          ],
        },
        {
          element: <CreateShipping />,
          path: AppRoutes.Products.CreateShipping,
        },
        {
          element: <Products.Receipts />,
          path: AppRoutes.Products.Receipts.Home,
          children: [
            {
              index: true,
              element: <Placeholder text="Please select a receipt" />,
            },
            {
              element: <Products.Exchange />,
              path: AppRoutes.Products.Receipts.Exchange,
            },
            {
              path: AppRoutes.Products.Receipts.Details,
              children: [
                {
                  index: true,
                  element: <Products.ReceiptDetails />,
                },
                ...(Config.isDebugHTMLEnabled
                  ? [
                      {
                        element: <Products.ReceiptHTML />,
                        path: AppRoutes.Products.Receipts.HTML,
                      },
                      {
                        path: AppRoutes.Products.Receipts.GiftHTML,
                        element: <Products.ReceiptHTML isGiftReceipt />,
                      },
                      {
                        element: <Products.RefundHTML />,
                        path: AppRoutes.Products.Receipts.RefundHTML,
                      },
                    ]
                  : []),
              ],
            },
          ],
        },
      ],
    },
    {
      title: 'Employees',
      hideForRoles: salesRoles,
      icon: <Icon.GroupFilled />,
      features: [Feature.EMPLOYEES],
      path: AppRoutes.Employees.Home,
      children: [
        {
          index: true,
          element: <Employees.List />,
        },
        {
          element: <Employees.Create />,
          path: AppRoutes.Employees.New,
          features: [Feature.EMPLOYEE_CREATE],
        },
        {
          element: <Employees.Edit />,
          path: AppRoutes.Employees.Edit,
          features: [Feature.EMPLOYEE_CREATE],
        },
        {
          path: AppRoutes.Employees.Details,
          element: <Profile.Home isEmployee />,
          children: getCommonProfileRoutes(true),
        },
      ],
    },
    {
      icon: <Icon.Trophy />,
      title: 'Rep Scoreboard',
      // REP PERFORMANCE (owner 25 Oct 2026): the role's rep_scoreboard key
      // (Permissions); money is by role on the server (none for a Manager).
      repPerformanceOnly: true,
      features: [Feature.REP_SCOREBOARD],
      path: AppRoutes.RepScoreboard.Home,
      children: [{ index: true, element: <RepScoreboardPage /> }],
    },
    {
      title: 'Training',
      // TRAINING (owner 25 Oct 2026, F-20 S3): readers see every trainer's
      // reps; a trainer of any role sees their own (the sidebar asks
      // /training/me). The page checks again on the server.
      trainingOnly: true,
      icon: <Icon.Certificate />,
      path: AppRoutes.Training.Home,
      children: [{ index: true, element: <TrainingPage /> }],
    },
    {
      icon: <Icon.Star />,
      title: 'Competition',
      element: <EmployeeCompetition />,
      path: AppRoutes.EmployeeCompetition.Home,
      features: [Feature.EMPLOYEE_COMPETITION],
    },

    {
      title: 'Shipments',
      element: <Shipments />,
      icon: <Icon.Transfer />,
      features: [Feature.SHIPMENTS],
      path: AppRoutes.Shipments.Home,
      children: [
        {
          index: true,
          element: (
            <Navigate
              replace
              to={AppRoutes.Shipments.Children.EventOrders.Home}
            />
          ),
        },
        {
          path: AppRoutes.Shipments.Children.EventOrders.Home,
          children: [
            {
              index: true,
              element: <EventOrders.List />,
            },
          ],
        },
        {
          path: AppRoutes.Shipments.Children.ExchangeOrders.Home,
          children: [
            {
              index: true,
              element: <EventOrders.List isExchange />,
            },
          ],
        },
      ],
    },
    {
      title: 'Users',
      features: [Feature.USERS],
      icon: <Icon.GroupFilled />,
      path: AppRoutes.Users.Home,
      /**
       * Added to hide tab for company owner.
       * If you remove users permission from company owner on the backend, it won't be possible to create a new employee.
       */
      hideForRoles: [Roles.COMPANY_OWNER, BackOfficeRoles.ADMIN_REP],
      children: [
        {
          index: true,
          element: <Users.List />,
        },
        {
          element: <Users.Create />,
          path: AppRoutes.Users.New,
        },
        {
          element: <Users.Edit />,
          path: AppRoutes.Users.Edit,
        },
        {
          element: <Users.Details />,
          path: AppRoutes.Users.Details,
        },
      ],
    },
    {
      // TIME OFF (owner 24 Oct 2026): approvers only, the role's `time_off`
      // tick (owner 2 Nov 2026, Permissions; it was a fixed role list).
      title: 'Time off',
      icon: <Icon.CalendarCheck />,
      path: AppRoutes.TimeOff.Home,
      features: [Feature.TIME_OFF],
      children: [{ index: true, element: <TimeOffPage /> }],
    },
    {
      title: 'Documents',
      // DOCUMENTS (plan 11 D2, 26 Oct 2026): Home · Agreements · Templates,
      // and a package's page; the `documents` permission (defaults Company
      // Owner, Site Admin, Administrative) shows it and guards the route.
      // A 24px menu icon (Icon.Document is the 312px empty-state picture).
      icon: <Icon.FileList />,
      path: DocumentsPaths.Home,
      element: <DocumentsLayout />,
      features: [Feature.DOCUMENTS],
      children: [
        { index: true, element: <DocumentsHomePage /> },
        {
          path: DocumentsPaths.Agreements,
          element: <DocumentsAgreementsPage />,
        },
        { path: DocumentsPaths.Package, element: <DocumentPackagePage /> },
        {
          path: DocumentsPaths.Templates,
          element: <DocumentsTemplatesPage />,
        },
        {
          path: DocumentsPaths.SetEditor,
          element: <DocumentsSetEditorPage />,
        },
        { path: DocumentsPaths.Send, element: <DocumentsSendPage /> },
        // Owner 26 Oct: the template editor and the order live in Documents
        // (not Company Settings -> Integrations).
        {
          element: <OnboardingOrderPage />,
          path: `${DocumentsPaths.Templates}/order`,
        },
        {
          element: <NewTemplatePage />,
          path: `${DocumentsPaths.Templates}/new`,
        },
        {
          element: <OnboardingTemplateEditor />,
          path: `${DocumentsPaths.Templates}/:templateId/:versionId`,
        },
        {
          element: <TemplateOrderStep />,
          path: `${DocumentsPaths.Templates}/:templateId/:versionId/order`,
        },
        {
          element: <TemplatePublishStep />,
          path: `${DocumentsPaths.Templates}/:templateId/:versionId/publish`,
        },
      ],
    },
    {
      title: 'Booking',
      // BOOKING (owner 25 Oct 2026, no company switch): the role's `booking`
      // tick (owner 30 Oct 2026, Permissions; defaults Company Owner,
      // Administrative and the Research roles); the page sends anyone else
      // home.
      bookingOnly: true,
      icon: <Icon.Queue />,
      element: <BookingLayout />,
      features: [Feature.BOOKING],
      path: AppRoutes.Booking.Home,
      hideForRoles: [Roles.SITE_ADMIN, Roles.BACKOFFICE],
      children: [
        {
          index: true,
          element: <Navigate replace to={AppRoutes.Booking.Queue} />,
        },
        {
          path: AppRoutes.Booking.Queue,
          children: [
            { index: true, element: <BookingQueuePage /> },
            { element: <BookingQueuePage />, path: AppRoutes.Booking.Details },
          ],
        },
        { path: AppRoutes.Booking.YearPlan, element: <BookingYearPlanPage /> },
        { path: AppRoutes.Booking.Payments, element: <BookingPaymentsPage /> },
      ],
    },
    {
      title: 'Research',
      // RESEARCH (owner 28 Oct 2026): its own list right under Booking;
      // never mixed with current events. The role's `research` tick, a
      // separate one from Booking (owner 30 Oct 2026).
      researchOnly: true,
      icon: <Icon.Search />,
      element: <ResearchLayout />,
      features: [Feature.RESEARCH],
      path: AppRoutes.Research.Home,
      hideForRoles: [Roles.SITE_ADMIN, Roles.BACKOFFICE],
      children: [
        { index: true, element: <ResearchListPage /> },
        { element: <ResearchListPage />, path: AppRoutes.Research.Details },
      ],
    },
    {
      // RESEARCH MAIL: the old /mail address opens the Mail tab inside
      // Research (owner 3 Nov: "It should be inside of the research"); no
      // sidebar item.
      hideInSidebar: true,
      element: <MailPage />,
      path: AppRoutes.Mail.Home,
    },
    {
      icon: <Icon.Calendar />,
      title: 'Events Calendar',
      features: [Feature.EVENTS_CALENDAR],
      path: AppRoutes.EventsCalendar.Home,
      children: [
        {
          index: true,
          element: <EventsCalendar.Calendar />,
        },
        {
          element: <EventsCalendar.TeamWeek />,
          path: AppRoutes.EventsCalendar.TeamWeek,
        },
      ],
    },
    {
      title: 'Trips',
      tripsOnly: true,
      icon: <Icon.Case />,
      path: AppRoutes.Trips.Home,
      children: [
        { index: true, element: <TripsPage /> },
        { element: <TripPersonPage />, path: AppRoutes.Trips.Person },
      ],
    },
    {
      title: 'My trip',
      myTripOnly: true,
      // One "Trips" entry opens these for a person who does not plan trips.
      hideInSidebar: true,
      icon: <Icon.Route />,
      path: AppRoutes.MyTrip.Home,
      children: [
        { index: true, element: <MyTripPage /> },
        { path: AppRoutes.MyTrip.Event, element: <MyTripEventPage /> },
        { path: AppRoutes.MyTrip.Item, element: <MyTripItemPage /> },
        { path: AppRoutes.MyTrip.Stock, element: <MyTripStockPage /> },
      ],
    },
    {
      title: 'Event Reviews',
      icon: <Icon.CheckCircle />,
      features: [Feature.EVENT_REVIEWS],
      path: AppRoutes.EventReviews.Home,
      children: [
        {
          index: true,
          element: <EventReviews.List />,
        },
        {
          element: <EventReviews.Details />,
          path: AppRoutes.EventReviews.Details,
        },
      ],
    },
    {
      icon: <Icon.Settings />,
      title: 'Company Settings',
      element: <CompanySettings.Home />,
      path: AppRoutes.CompanySettings.Home,
      hideForRoles: [
        ...Object.values(BackOfficeRoles),
        Roles.BACKOFFICE,
        Roles.SITE_ADMIN,
      ],
      children: [
        {
          index: true,
          element: (
            <Navigate replace to={AppRoutes.CompanySettings.BusinessInfo} />
          ),
        },
        {
          element: <CompanySettings.BusinessInfo />,
          path: AppRoutes.CompanySettings.BusinessInfo,
        },
        {
          element: <CompanySettings.Permissions />,
          path: AppRoutes.CompanySettings.Permissions,
        },
        {
          element: <CompanySettings.Notifications />,
          path: AppRoutes.CompanySettings.Notifications,
        },
        {
          element: <CompanySettings.Receipts />,
          path: AppRoutes.CompanySettings.Receipts,
        },
        {
          element: <CompanySettings.Integrations />,
          path: AppRoutes.CompanySettings.Integrations,
        },
        {
          features: [Feature.QUICKBOOKS],
          element: <QuickBooksSettingsPage />,
          path: `${AppRoutes.CompanySettings.Integrations}/quickbooks`,
        },
        {
          element: <CompanySettings.Devices />,
          path: AppRoutes.CompanySettings.Devices,
        },
        {
          element: <CompanySettings.Packages />,
          path: AppRoutes.CompanySettings.Packages,
        },
        {
          element: <CompanySettings.PaymentMethods />,
          path: AppRoutes.CompanySettings.PaymentMethods,
        },
        {
          // POS health (owner 18 Oct 2026): Company Owner only; the server
          // refuses every other role.
          element: <CompanySettings.PosHealth />,
          path: AppRoutes.CompanySettings.PosHealth,
          hideForRoles: [
            ...Object.values(BackOfficeRoles),
            Roles.BACKOFFICE,
            Roles.SITE_ADMIN,
          ],
        },
        Config.isDebugHTMLEnabled
          ? {
              element: <Products.ReceiptHTML />,
              path: `${AppRoutes.CompanySettings.Receipts}/${AppRoutes.CompanySettings.HTML}`,
            }
          : {},
      ],
    },
    {
      title: 'Settings',
      element: <Settings />,
      icon: <Icon.Settings />,
      path: AppRoutes.Settings.Home,
      features: [
        Feature.THEMES,
        Feature.PRODUCT_TYPES,
        Feature.PAYMENT_METHODS,
      ],
      hideForRoles: [
        Roles.BACKOFFICE,
        Roles.COMPANY_OWNER,
        ...Object.values(BackOfficeRoles),
      ],
      children: [
        {
          index: true,
          element: (
            <Navigate replace to={AppRoutes.Settings.Children.ProductTypes} />
          ),
        },
        {
          element: <ProductTypes />,
          path: AppRoutes.Settings.Children.ProductTypes,
        },
        {
          element: <Themes />,
          path: AppRoutes.Settings.Children.Themes,
        },
        {
          element: <Carriers />,
          path: AppRoutes.Settings.Children.Carriers,
        },
        {
          element: <Shipping />,
          path: AppRoutes.Settings.Children.Shipping,
        },
        {
          element: <Integrations />,
          path: AppRoutes.Settings.Children.Integrations,
        },
        {
          element: <Devices />,
          path: AppRoutes.Settings.Children.Devices,
        },
      ],
    },
    {
      hideInSidebar: true,
      title: 'Notifications',
      icon: <Icon.BankCardCheck />,
      features: [Feature.NOTIFICATIONS],
      path: AppRoutes.Notifications.Home,
      children: [
        {
          index: true,
          element: <Notifications.List />,
        },
      ],
    },
  ];
};
